import { useEffect, useState } from 'react';
import { WifiOff, Download, X, Share, Plus } from 'lucide-react';
import { lsGet, lsSet, lsDel } from '../lib/api';

/**
 * PwaStatusBar — surfaces three PWA states:
 *  1. Offline indicator (navigator.onLine === false).
 *  2. Install prompt for Chromium/Edge/Android (beforeinstallprompt).
 *  3. iOS Safari install instructions (since Apple never fires beforeinstallprompt).
 *
 * All bars are dismissible and non-intrusive: bottom-right on desktop, full-width
 * at the bottom on mobile.
 */
function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIPhone = /iPhone|iPad|iPod/i.test(ua);
  // iPadOS 13+ reports as Mac — sniff via touch points.
  const isIPadOnMac = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return isIPhone || isIPadOnMac;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Older iOS Safari
    window.navigator.standalone === true
  );
}

export default function PwaStatusBar() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const snoozeLeft = () => {
    try { return Number(lsGet('sahakar_install_snooze') || 0) > Date.now(); }
    catch { return false; /* noop */ }
  };
  const [installDismissed, setInstallDismissed] = useState(snoozeLeft);
  const [asked, setAsked] = useState(false);
  const [iosHintDismissed, setIosHintDismissed] = useState(() => {
    try { return lsGet('sahakar_ios_hint_dismissed') === '1'; } catch { return false; /* noop */ }
  });

  const ios = detectIOS();
  const standalone = isStandalone();

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setInstallPrompt(null);
      setInstallDismissed(true);
      setAsked(false);
    };
    // "Add to home screen" in the sidebar brings this back even after it was snoozed
    const onAsk = () => {
      try { lsDel('sahakar_install_snooze');
            lsDel('sahakar_ios_hint_dismissed'); } catch { /* noop */ }
      setInstallDismissed(false);
      setIosHintDismissed(false);
      setAsked(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('sahakar:install-ask', onAsk);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('sahakar:install-ask', onAsk);
    };
  }, []);

  const SNOOZE_DAYS = 14;
  const snooze = () => {
    try { lsSet('sahakar_install_snooze', String(Date.now() + SNOOZE_DAYS * 864e5)); }
    catch { /* noop */ }
  };

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    let outcome = '';
    try { ({ outcome } = await installPrompt.userChoice); } catch { /* noop */ }
    if (outcome !== 'accepted') snooze();
    setInstallPrompt(null);
    setInstallDismissed(true);
    setAsked(false);
  };

  const dismissInstall = () => {
    setInstallPrompt(null);
    setInstallDismissed(true);
    setAsked(false);
    snooze();
  };

  const dismissIos = () => {
    setIosHintDismissed(true);
    setAsked(false);
    try { lsSet('sahakar_ios_hint_dismissed', '1'); } catch { /* noop */ }
    snooze();
  };

  // Steps are shown on iOS, and to anyone who asked for it from the sidebar with no native prompt waiting
  const showIosHint = !standalone && !iosHintDismissed && (ios || asked) && !installPrompt;

  return (
    <>
      {!online && (
        <div
          data-testid="pwa-offline-banner"
          className="fixed bottom-0 left-0 right-0 z-[60] md:left-auto md:right-4 md:bottom-4 md:max-w-sm md:rounded-xl px-4 py-3 pwa-safe-bottom bg-amber-500/95 text-amber-950 shadow-2xl flex items-center gap-3 border-t md:border border-amber-600/40 backdrop-blur"
        >
          <WifiOff size={18} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">You&apos;re offline</div>
            <div className="text-xs opacity-80 truncate">Showing the last loaded data. New actions will retry when you reconnect.</div>
          </div>
        </div>
      )}

      {online && installPrompt && (!installDismissed || asked) && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-0 left-0 right-0 z-[55] md:left-auto md:right-4 md:bottom-4 md:max-w-sm md:rounded-xl px-4 py-3 pwa-safe-bottom bg-card border-t md:border border-border shadow-2xl flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center shrink-0">
            <Download size={17} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Keep Sahakar on your home screen</div>
            <div className="text-xs text-muted-foreground">Opens full screen like an app — faster, and it works offline.</div>
          </div>
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-accent text-accent-foreground hover:opacity-90 transition-opacity"
          >Add</button>
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismissInstall}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground"
            aria-label="Dismiss"
          ><X size={15} /></button>
        </div>
      )}

      {online && showIosHint && !installPrompt && (
        <div
          data-testid="pwa-ios-install-banner"
          className="fixed bottom-0 left-0 right-0 z-[55] md:left-auto md:right-4 md:bottom-4 md:max-w-sm md:rounded-xl px-4 py-3 pwa-safe-bottom bg-card border-t md:border border-border shadow-2xl"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <Download size={17} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">
                {ios ? 'Add Sahakar to your iPhone' : 'Add Sahakar to your home screen'}
              </div>
              {ios ? (
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Tap the <Share size={12} className="inline align-text-bottom mx-0.5"/> <span className="font-semibold text-foreground">Share</span> button in Safari, then choose <span className="font-semibold text-foreground"><Plus size={11} className="inline align-text-bottom"/> Add to Home Screen</span>.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Open your browser menu and choose <span className="font-semibold text-foreground"><Plus size={11} className="inline align-text-bottom"/> Install app</span> (or <span className="font-semibold text-foreground">Add to Home screen</span>).
                </div>
              )}
              <div className="text-[11px] text-muted-foreground/80 mt-1.5">
                {ios ? 'Use Safari for this. ' : ''}Once added, Sahakar opens full screen like an app and works offline.
              </div>
            </div>
            <button
              data-testid="pwa-ios-hint-dismiss"
              onClick={dismissIos}
              className="p-1 rounded-md hover:bg-muted text-muted-foreground shrink-0"
              aria-label="Dismiss"
            ><X size={15} /></button>
          </div>
        </div>
      )}
    </>
  );
}
